/**
 * NotificationLogAdmin — /admin/notification-log (#1049)
 * Audit-Trail mit Filter + CSV-Export.
 */
import { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Activity, Download, Filter, RefreshCw } from 'lucide-react';

const API = '';

const STATUS_COLORS = {
  sent: 'bg-emerald-500/10 text-emerald-700',
  failed: 'bg-red-500/10 text-red-700',
  skipped: 'bg-amber-500/10 text-amber-700',
  blocked: 'bg-slate-500/10 text-slate-700',
};

export default function NotificationLogAdmin() {
  const { t, language } = useLanguage();
  const dateLocale = (language || 'de') === 'de' ? 'de-AT' : 'en-GB';
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [filters, setFilters] = useState({
    event_key: '', status: '', kanal: '', search: '', from: '', to: '',
  });
  const [page, setPage] = useState(0);
  const limit = 50;

  function buildQuery() {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    params.set('limit', String(limit));
    params.set('offset', String(page * limit));
    return params.toString();
  }

  async function load() {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/notification-log?${buildQuery()}`, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.error || `HTTP ${r.status}`);
      setEntries(Array.isArray(d.entries) ? d.entries : []);
      setTotal(d.total || 0);
      setLoadError(null);
    } catch (err) {
      setEntries([]);
      setTotal(0);
      setLoadError(err.message || String(err));
    } finally { setLoading(false); }
  }

  async function loadStats() {
    try {
      const r = await fetch(`${API}/api/admin/notification-log/stats`, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.error || `HTTP ${r.status}`);
      setStats(Array.isArray(d?.by_status_kanal) ? d : null);
    } catch {
      setStats(null);
    }
  }

  useEffect(() => { load(); }, [page, filters.status, filters.kanal]);
  // #1800: loadStats ist async — die zurückgegebene Promise würde React 19 beim
  // Unmount als Cleanup-Funktion aufrufen → TypeError → Error-Boundary-Crash.
  useEffect(() => { loadStats(); }, []);

  function exportCsv() {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    window.location.href = `${API}/api/admin/notification-log/export?${params.toString()}`;
  }

  function setF(k, v) { setFilters(prev => ({ ...prev, [k]: v })); setPage(0); }

  return (
    <div className="p-6 w-full max-w-screen-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--primary,#338edc)]/10 flex items-center justify-center">
            <Activity size={20} className="text-[var(--primary,#338edc)]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">{t('notifLogAdmin.title')}</h1>
            <p className="text-sm text-[var(--text-tertiary)]">{t('notifLogAdmin.subtitle')}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { load(); loadStats(); }} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[var(--border-card)] text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]">
            <RefreshCw size={12} /> {t('common.refresh')}
          </button>
          <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[var(--primary,#338edc)] text-white text-xs font-medium hover:bg-[var(--primary,#338edc)]/90">
            <Download size={12} /> CSV
          </button>
        </div>
      </div>

      {/* Fehlerdetails statt Blank-Boundary (#1800) */}
      {loadError && (
        <div className="mb-4 rounded-xl p-3 text-sm" style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B' }}>
          {t('notifLogAdmin.loadError')}: <span className="font-mono text-xs">{loadError}</span>
        </div>
      )}

      {/* Stats-Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {['sent', 'skipped', 'failed', 'blocked'].map(st => {
            const total = stats.by_status_kanal.filter(s => s.status === st).reduce((sum, s) => sum + s.cnt, 0);
            return (
              <div key={st} className="border border-[var(--border-card)] rounded-xl bg-[var(--bg-card,#fff)] p-4">
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1">{t(`notifLogAdmin.status_${st}`)}</div>
                <div className="text-xl font-bold text-[var(--text-primary)]">{total.toLocaleString()}</div>
                <div className="text-[10px] text-[var(--text-tertiary)] mt-1">{t('notifLogAdmin.last30days')}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filter-Bar */}
      <div className="border border-[var(--border-card)] rounded-xl bg-[var(--bg-card,#fff)] p-3 mb-4">
        <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-[var(--text-secondary)]">
          <Filter size={12} /> {t('notifLogAdmin.filters')}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <input value={filters.event_key} onChange={e => setF('event_key', e.target.value)} placeholder={t('notifLogAdmin.eventKey')} className="px-2 py-1.5 bg-[var(--bg-input)] border border-[var(--border-input)] rounded text-xs" />
          <select value={filters.status} onChange={e => setF('status', e.target.value)} className="px-2 py-1.5 bg-[var(--bg-input)] border border-[var(--border-input)] rounded text-xs">
            <option value="">{t('notifLogAdmin.allStatuses')}</option>
            <option value="sent">{t('notifLogAdmin.status_sent')}</option>
            <option value="failed">{t('notifLogAdmin.status_failed')}</option>
            <option value="skipped">{t('notifLogAdmin.status_skipped')}</option>
            <option value="blocked">{t('notifLogAdmin.status_blocked')}</option>
          </select>
          <select value={filters.kanal} onChange={e => setF('kanal', e.target.value)} className="px-2 py-1.5 bg-[var(--bg-input)] border border-[var(--border-input)] rounded text-xs">
            <option value="">{t('notifLogAdmin.allChannels')}</option>
            <option value="email">{t('notifLogAdmin.channel_email')}</option>
            <option value="in_app">{t('notifLogAdmin.channel_in_app')}</option>
            <option value="sms">{t('notifLogAdmin.channel_sms')}</option>
            <option value="telegram">{t('notifLogAdmin.channel_telegram')}</option>
            <option value="all">{t('notifLogAdmin.channel_all_skipped')}</option>
          </select>
          <input value={filters.search} onChange={e => setF('search', e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} placeholder={t('common.search')} className="px-2 py-1.5 bg-[var(--bg-input)] border border-[var(--border-input)] rounded text-xs" />
          <input type="date" value={filters.from} onChange={e => setF('from', e.target.value)} className="px-2 py-1.5 bg-[var(--bg-input)] border border-[var(--border-input)] rounded text-xs" />
          <input type="date" value={filters.to} onChange={e => setF('to', e.target.value)} className="px-2 py-1.5 bg-[var(--bg-input)] border border-[var(--border-input)] rounded text-xs" />
        </div>
        <button onClick={load} className="mt-2 px-3 py-1.5 rounded bg-[var(--primary,#338edc)] text-white text-xs font-medium hover:bg-[var(--primary,#338edc)]/90">
          {t('common.apply')}
        </button>
      </div>

      {/* Tabelle */}
      <div className="border border-[var(--border-card)] rounded-xl bg-[var(--bg-card,#fff)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[var(--bg-inset)] text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                <th className="px-3 py-2 text-left">{t('notifLogAdmin.time')}</th>
                <th className="px-3 py-2 text-left">{t('notifLogAdmin.event')}</th>
                <th className="px-3 py-2 text-left">{t('notifLogAdmin.user')}</th>
                <th className="px-3 py-2 text-left">{t('notifLogAdmin.channel')}</th>
                <th className="px-3 py-2 text-left">{t('notifLogAdmin.status')}</th>
                <th className="px-3 py-2 text-left">{t('notifLogAdmin.reason')}</th>
                <th className="px-3 py-2 text-left">{t('notifLogAdmin.reference')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-card)]/50">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--text-tertiary)]">{t('common.loading')}…</td></tr>
              )}
              {!loading && entries.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--text-tertiary)]">{t('notifLogAdmin.noEntries')}</td></tr>
              )}
              {entries.map(e => (
                <tr key={e.id} className="hover:bg-[var(--bg-inset)]">
                  <td className="px-3 py-2 text-[var(--text-tertiary)] whitespace-nowrap">
                    {new Date(e.created_at).toLocaleString(dateLocale, { dateStyle: 'short', timeStyle: 'medium' })}
                  </td>
                  <td className="px-3 py-2 font-mono text-[var(--text-primary)]">{e.event_key}</td>
                  <td className="px-3 py-2">
                    {e.user_id ? (
                      <span className="text-[var(--text-secondary)]">{e.vorname} {e.nachname} <span className="text-[10px] text-[var(--text-tertiary)]">#{e.user_id}</span></span>
                    ) : <span className="text-[var(--text-tertiary)]">—</span>}
                  </td>
                  <td className="px-3 py-2"><span className="px-1.5 py-0.5 rounded bg-[var(--bg-inset)] text-[10px] font-mono">{e.kanal}</span></td>
                  <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${STATUS_COLORS[e.status] || 'bg-gray-100'}`}>{e.status}</span></td>
                  <td className="px-3 py-2 text-[var(--text-secondary)]">{e.grund || '—'}</td>
                  <td className="px-3 py-2 text-[var(--text-tertiary)] font-mono">{e.referenz_typ ? `${e.referenz_typ}/${e.referenz_id}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-[var(--border-card)] text-xs text-[var(--text-tertiary)]">
          <span>{t('notifLogAdmin.showing')} {entries.length} {t('common.of')} {total.toLocaleString()}</span>
          <div className="flex gap-1">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="px-2 py-1 rounded border border-[var(--border-card)] disabled:opacity-30">‹</button>
            <span className="px-2 py-1">{page + 1} / {Math.max(1, Math.ceil(total / limit))}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total} className="px-2 py-1 rounded border border-[var(--border-card)] disabled:opacity-30">›</button>
          </div>
        </div>
      </div>
    </div>
  );
}
